import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { addYears } from "date-fns";
import MHeader from "@/components/ui/organisms/MHeader";
import headerStyles from "@/components/ui/organisms/MHeader.module.css";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { MButton } from "@/components/ui/atoms/MButtons";
import MTimePicker, {
  parseTimeString,
  formatTimeString,
} from "@/components/ui/atoms/MTimePicker";
import { IconClock, IconRefresh } from "@tabler/icons-react";
import { MemberInfo } from "@/types/member";
import { DEFAULT_YEARS_AHEAD } from "@/utils/constants";
import {
  paintReminderDateField,
  paintReminderEndDateField,
  paintReminderNotesField,
} from "./remindersHelper";
import styles from "./ReminderSummary.module.css";

export interface ReminderSummaryTimeSlot {
  value: string | null;
  onChange: (value: string) => void;
  label: string;
  /** Set false for a single one-off reminder time (e.g. an appointment) —
   *  hides the numbered badge and centers the chip's contents instead.
   *  Defaults to true, matching the dose-schedule chips (vitals, medication). */
  showBadge?: boolean;
}

interface TimeChipProps extends ReminderSummaryTimeSlot {
  t: TFunction;
}

// A single "Reminder time" slot — a compact pill (badge + time + clock),
// not a bordered MTimeInputField, matching the Figma chip spec. Tapping it
// opens the time picker directly.
const TimeChip: React.FC<TimeChipProps> = ({
  label,
  value,
  onChange,
  t,
  showBadge = true,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className={`${styles.timeChip} ${
          !showBadge ? styles.timeChipCentered : ""
        }`}
        onClick={() => setOpen(true)}
      >
        {showBadge && <span className={styles.timeChipBadge}>{label}</span>}
        <span
          className={
            value ? styles.timeChipValue : styles.timeChipValuePlaceholder
          }
        >
          {value || t("common.notAnswered")}
        </span>
        <IconClock size={16} className={styles.timeChipClock} />
      </button>
      <MTimePicker
        open={open}
        onClose={() => setOpen(false)}
        onDone={(date) => {
          onChange(formatTimeString(date, 12));
          setOpen(false);
        }}
        initialValue={parseTimeString(value ?? undefined, 12)}
        title={t("common.selectTime")}
        format={12}
        minuteStep={15}
      />
    </>
  );
};

export interface ReminderSummaryScreenProps {
  memberInfo: MemberInfo;
  title: string;
  description: string;
  /** Namespace passed to the shared date/end-date/notes field helpers —
   *  e.g. "vitals", "medication" — so each reminder type's own i18n copy
   *  (reminders.{labelKey}.reminderDateLabel etc.) is used. */
  labelKey: string;
  /** Recap card at the top of the content: icon + label + a secondary line
   *  (frequency for vitals/medication, "Appointment on {date}" for
   *  appointment types, etc.). */
  recapIcon: React.ReactNode;
  recapIconClass: string;
  recapLabel: string;
  recapSecondaryText: string;
  /** Icon shown before recapSecondaryText — defaults to the refresh icon
   *  (frequency callers). Pass null to omit it entirely (e.g. a plain
   *  "Appointment on ..." line has no icon in Figma). */
  recapSecondaryIcon?: React.ReactNode | null;
  /** Extra class merged onto the secondary line — lets callers override the
   *  default bold/dark styling (e.g. appointment's lighter caption). */
  recapSecondaryTextClassName?: string;
  /** Optional third recap line, plain caption styling, no icon — e.g.
   *  insurance's "Valid until {date}" below the primary holder's name
   *  (Figma 5257-62339). Omit for types with only two recap lines. */
  recapTertiaryText?: string;
  reminderDate: Date | null;
  setReminderDate: (date: Date | null) => void;
  /** Hide the end-date field entirely — one-off appointments have no
   *  recurrence, so there's nothing to end. Defaults to true. */
  showEndDate?: boolean;
  reminderEndDate: Date | null;
  setReminderEndDate: (date: Date | null) => void;
  timeSlots: ReminderSummaryTimeSlot[];
  notes: string | null;
  setNotes: (notes: string | null) => void;
  isInvalid: boolean;
  onSubmit: () => void;
  submitLabel?: string;
  /** Per-type extra rendered inside the white content card, above the
   *  reminder-date field — e.g. medication's "Allow Critical Alerts"
   *  toggle (Figma 5257-62452). Omit for types with nothing extra. */
  topContent?: React.ReactNode;
}

// The shared "final"/summary screen used across every reminder type —
// recap card, start/end date, reminder-time chips, notes, and the submit
// CTA, over the off-white page + curve background. Each reminder type
// supplies its own copy, icon, and recap frequency text; the layout,
// spacing, and field styling are identical across types per Figma.
const ReminderSummaryScreen: React.FC<ReminderSummaryScreenProps> = ({
  memberInfo,
  title,
  description,
  labelKey,
  recapIcon,
  recapIconClass,
  recapLabel,
  recapSecondaryText,
  recapSecondaryIcon,
  recapSecondaryTextClassName,
  recapTertiaryText,
  reminderDate,
  setReminderDate,
  showEndDate = true,
  reminderEndDate,
  setReminderEndDate,
  timeSlots,
  notes,
  setNotes,
  isInvalid,
  onSubmit,
  submitLabel,
  topContent,
}) => {
  const { t } = useTranslation();
  const maxDate = addYears(new Date(), DEFAULT_YEARS_AHEAD);

  return (
    <div className="relative w-full h-full flex flex-col">
      <MHeader
        module="reminders"
        memberInfo={memberInfo}
        title={title}
        description={description}
        // The page-level curve (.summaryPageBackground, applied by the
        // caller to the outer wrapper containing MTopBar + this screen) is
        // the same proven pattern medication's own final stage already
        // uses. stickyBelowTopBarWithGradient gives MHeader the matching
        // opaque purple gradient instead of white, so the header layer
        // blends with the curve instead of sitting on top of it as a
        // visible box — pair with MTopBar's showPurpleGradient on the
        // final stage for the same reason.
        className={headerStyles.stickyBelowTopBarWithGradient}
      />
      {/* Figma: text-header (5257-60142) and body-wrapper (5257-60143) —
          body-wrapper starts 16px below the header, not the default
          MSpacer's 64px. */}
      <MSpacer mt={16} />
      {/* No flex-1 here — the content should size to its own fields, not
          stretch to fill leftover viewport height. .submitButtonRow's own
          margin-top:auto is what consumes any leftover space (pushing the
          button to the bottom on short-content screens), so stretching
          the content here too would just spread the fields apart instead
          of leaving that space to the button where it belongs. */}
      <div className="profile-input-container flex flex-col gap-4">
        <div className={styles.recapCard}>
          <div className={`${styles.recapIcon} ${recapIconClass}`}>
            {recapIcon}
          </div>
          <div className={styles.recapText}>
            <span className={styles.recapLabel}>{recapLabel}</span>
            {recapSecondaryText && (
              <span
                className={`${styles.recapFrequency} ${
                  recapSecondaryTextClassName || ""
                }`}
              >
                {recapSecondaryIcon === undefined ? (
                  <IconRefresh
                    size={16}
                    className={styles.recapFrequencyIcon}
                  />
                ) : (
                  recapSecondaryIcon
                )}
                {recapSecondaryText}
              </span>
            )}
            {recapTertiaryText && (
              <span className={styles.recapTertiary}>{recapTertiaryText}</span>
            )}
          </div>
        </div>
        <div className={styles.contentCard}>
          {topContent}
          {paintReminderDateField(
            reminderDate,
            setReminderDate,
            maxDate,
            t,
            labelKey,
            true,
            true
          )}
          <div className={styles.timeSlotBox}>
            <div className={styles.timeSlotRow}>
              {timeSlots.map((slot, index) => (
                <TimeChip
                  key={`${slot.label}-${index}`}
                  label={slot.label}
                  value={slot.value}
                  onChange={slot.onChange}
                  showBadge={slot.showBadge}
                  t={t}
                />
              ))}
            </div>
          </div>
          {showEndDate &&
            paintReminderEndDateField(
              reminderEndDate,
              setReminderEndDate,
              maxDate,
              reminderDate,
              t,
              labelKey,
              true,
              true
            )}
          {paintReminderNotesField(
            notes,
            setNotes,
            t,
            labelKey,
            true,
            undefined,
            true
          )}
        </div>
      </div>
      {/* Figma measures this as a flat 26px gap from the textarea to the
          button itself (button's own 20px/28px padding is internal to the
          button component) — a normal-flow element right after the
          content, NOT a position:fixed footer pinned to the viewport
          bottom. A fixed footer is the right call for the type/details
          picker stages (Next always at the true bottom regardless of how
          little content there is), but it structurally can't produce "26px
          below whatever the content's real height happens to be" — those
          two things are independent once the button is unpinned from
          flow. */}
      <div className={styles.submitButtonRow}>
        <MButton
          variant="filled"
          size="lg"
          onClick={onSubmit}
          className="w-full"
          disabled={isInvalid}
        >
          {submitLabel || t("reminders.setReminder")}
        </MButton>
      </div>
    </div>
  );
};

export default ReminderSummaryScreen;
